import { useIsWorkspaceOnboardingRenameEnabled } from '@/features/flags';
import { useWorkspaceNameDefault } from '@/hooks/useWorkspaceNameDefault';
import { useEffect, useRef } from 'react';

import type { UsePersonalizationFormReturn } from '@heidi/growth';

/**
 * Pre-fills the repurposed "Workspace name" field (clinicName) with the
 * localised `<first name>'s workspace` default once the user reaches the clinic
 * step. The ref guard fires it a single time, so clearing the field afterwards
 * does not re-inject the default. Gated behind the rename flag.
 */
export function useWorkspaceNamePrefill(
  currentStep: string,
  firstName: string,
  clinicName: string,
  updateFormData: UsePersonalizationFormReturn['updateFormData']
) {
  const isEnabled = useIsWorkspaceOnboardingRenameEnabled();
  const buildWorkspaceName = useWorkspaceNameDefault();
  const hasPrefilled = useRef(false);

  useEffect(() => {
    if (!isEnabled || hasPrefilled.current || currentStep !== 'clinic') return;
    const trimmed = firstName.trim();
    if (trimmed && clinicName.trim() === '') {
      hasPrefilled.current = true;
      updateFormData('clinicName', buildWorkspaceName(trimmed));
    }
  }, [isEnabled, buildWorkspaceName, currentStep, firstName, clinicName, updateFormData]);
}
